import React from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, Lock, Trash2, Shield, Sparkles, Mic, Upload, Youtube, CheckSquare, Lightbulb } from 'lucide-react';
import { getStoreUrl } from '../utils/storeUrls';

/* ── CSS-art panels ─────────────────────────────────────────── */

const AudioSourceArt = () => {
  const { t } = useTranslation();

  const audioSources = [
    {
      icon: <Mic className="w-5 h-5" />,
      title: t('featureSections.audioFormat.art.record.title', 'Record live'),
      sub: t('featureSections.audioFormat.art.record.sub', 'Built-in recorder, tap to start'),
      active: true,
    },
    {
      icon: <Upload className="w-5 h-5" />,
      title: t('featureSections.audioFormat.art.upload.title', 'Upload a file'),
      sub: t('featureSections.audioFormat.art.upload.sub', 'MP3, WAV, M4A and more'),
      active: false,
    },
    {
      icon: <Youtube className="w-5 h-5" />,
      title: t('featureSections.audioFormat.art.youtube.title', 'Import from YouTube'),
      sub: t('featureSections.audioFormat.art.youtube.sub', "Paste a link, we'll transcribe it"),
      active: false,
    },
  ];

  return (
    <div
      style={{
        background: 'var(--v2-card)',
        border: '1px solid var(--v2-line)',
        borderRadius: '20px',
        padding: '32px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {audioSources.map((row, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px',
            borderRadius: '12px',
            background: row.active ? 'var(--v2-primary-light)' : 'var(--v2-card)',
            border: row.active ? '1px solid var(--v2-primary)' : '1px solid var(--v2-line)',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: row.active ? 'var(--v2-primary)' : 'var(--v2-bg-subtle)',
              color: row.active ? '#fff' : 'var(--v2-ink-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {row.icon}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--v2-ink)' }}>{row.title}</div>
            <div style={{ fontSize: '12.5px', color: 'var(--v2-ink-muted)', marginTop: '2px' }}>{row.sub}</div>
          </div>
          {row.active && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <span
                style={{
                  width: '6px', height: '6px', borderRadius: '50%',
                  background: '#ef4444', animation: 'blink 1.2s infinite',
                }}
              />
              <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--v2-primary)', letterSpacing: '0.03em' }}>
                {t('featureSections.audioFormat.art.live', 'REC')}
              </span>
            </div>
          )}
        </div>
      ))}
      <div
        style={{
          width: '100%',
          marginTop: '4px',
          padding: '12px 16px',
          borderRadius: '12px',
          background: 'var(--v2-primary-light)',
          border: '1px solid var(--v2-primary)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '13px',
          color: 'var(--v2-primary)',
          fontWeight: 600,
        }}
      >
        <span style={{ fontSize: '18px' }}>🎯</span>
        {t('featureSections.audioFormat.art.footer', '99% transcription accuracy · 30+ languages')}
      </div>
    </div>
  );
};

const privacyRows = [
  { icon: <Ban className="w-5 h-5" />, key: 'onDevice' },
  { icon: <Lock className="w-5 h-5" />, key: 'encrypted' },
  { icon: <Trash2 className="w-5 h-5" />, key: 'delete' },
  { icon: <Shield className="w-5 h-5" />, key: 'noSharing' },
];

const PrivacyArt = ({ inverted = false }: { inverted?: boolean }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: inverted ? 'rgba(255,255,255,0.08)' : 'var(--v2-card)',
        border: inverted ? '1px solid rgba(255,255,255,0.16)' : '1px solid var(--v2-line)',
        borderRadius: '20px',
        padding: '32px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {privacyRows.map((row, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px',
            borderRadius: '12px',
            background: inverted ? 'rgba(255,255,255,0.06)' : 'var(--v2-card)',
            border: inverted ? '1px solid rgba(255,255,255,0.14)' : '1px solid var(--v2-line)',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: inverted ? 'rgba(255,255,255,0.16)' : 'var(--v2-primary-light)',
              color: inverted ? '#fff' : 'var(--v2-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {row.icon}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px', color: inverted ? '#fff' : 'var(--v2-ink)' }}>{t(`featuresPage.privacy.rows.${row.key}.title`)}</div>
            <div style={{ fontSize: '12.5px', color: inverted ? 'rgba(255,255,255,0.75)' : 'var(--v2-ink-muted)', marginTop: '2px' }}>{t(`featuresPage.privacy.rows.${row.key}.sub`)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const SummaryGroup = ({
  label,
  icon,
  items,
}: {
  label: string;
  icon: React.ReactNode;
  items: string[];
}) => (
  <div style={{ marginBottom: '18px' }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '10px',
        color: 'var(--v2-primary)',
      }}
    >
      {icon}
      <span className="eyebrow" style={{ fontSize: '12px' }}>
        {label}
      </span>
    </div>
    {items.map((item, i) => (
      <div
        key={i}
        style={{
          background: 'var(--v2-bg-subtle)',
          borderRadius: '10px',
          padding: '10px 12px',
          fontSize: '13.5px',
          borderLeft: '3px solid var(--v2-primary)',
          marginBottom: '8px',
          color: 'var(--v2-ink-2)',
          lineHeight: 1.5,
        }}
      >
        {item}
      </div>
    ))}
  </div>
);

const SummaryArt = () => {
  const { t } = useTranslation();

  const actionItems = Array.isArray(t('featureSections.summaries.art.actionItems', { returnObjects: true }))
    ? (t('featureSections.summaries.art.actionItems', { returnObjects: true }) as string[])
    : [
        'Pull API deadline forward; demo at All-Hands on Apr 22',
        'Ravi owns beta retention numbers by Friday EOD',
      ];

  const keyInsights = Array.isArray(t('featureSections.summaries.art.keyInsights', { returnObjects: true }))
    ? (t('featureSections.summaries.art.keyInsights', { returnObjects: true }) as string[])
    : [
        'Free tier usage points to cutting it to 15 min/day, raising Pro to 10h',
        'Users keep asking for a bullet/paragraph toggle — shipping next release',
      ];

  return (
    <div
      style={{
        background: 'var(--v2-card)',
        border: '1px solid var(--v2-line)',
        borderRadius: '20px',
        padding: '24px',
        boxShadow: 'var(--v2-shadow-soft)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '9px',
            background: 'var(--v2-primary-light)',
            color: 'var(--v2-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Sparkles className="w-4 h-4" />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--v2-ink)' }}>
            {t('featureSections.summaries.art.meetingTitle', 'Q2 Roadmap Sync')}
          </div>
          <div
            style={{
              fontFamily: '"JetBrains Mono", ui-monospace, monospace',
              fontSize: '10.5px',
              fontWeight: 500,
              color: 'var(--v2-ink-muted)',
              letterSpacing: '0.03em',
            }}
          >
            {t('featureSections.summaries.art.meetingMeta', '58 MIN · APR 18')}
          </div>
        </div>
      </div>
      <SummaryGroup
        label={t('featureSections.summaries.art.actionItemsLabel', 'Action items')}
        icon={<CheckSquare className="w-3.5 h-3.5" />}
        items={actionItems}
      />
      <SummaryGroup
        label={t('featureSections.summaries.art.keyInsightsLabel', 'Key insights')}
        icon={<Lightbulb className="w-3.5 h-3.5" />}
        items={keyInsights}
      />
    </div>
  );
};

/* ── Shared section layout ─────────────────────────────────── */

interface SectionProps {
  label: string;
  title: string;
  description: string;
  features: string[];
  cta: string;
  ctaHref: string;
  art: React.ReactNode;
  reversed?: boolean;
  variant?: 'default' | 'indigo';
}

const FeatureSection = ({
  label,
  title,
  description,
  features,
  cta,
  ctaHref,
  art,
  reversed = false,
  variant = 'default',
}: SectionProps) => {
  const inverted = variant === 'indigo';

  const textCol = (
    <div>
      <div
        className="eyebrow"
        style={{
          color: inverted ? 'rgba(255,255,255,0.85)' : 'var(--v2-primary)',
          marginBottom: '12px',
        }}
      >
        {label}
      </div>
      <h2
        style={{
          fontSize: 'clamp(26px, 3vw, 38px)',
          fontWeight: 800,
          color: inverted ? '#fff' : 'var(--v2-ink)',
          marginBottom: '14px',
          lineHeight: 1.15,
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontSize: '16px',
          color: inverted ? 'rgba(255,255,255,0.85)' : 'var(--v2-ink-muted)',
          marginBottom: '24px',
          lineHeight: 1.65,
        }}
      >
        {description}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '32px' }}>
        {features.map((feature, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: inverted ? '#fff' : 'var(--v2-primary)',
                marginTop: '7px',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: '14px', color: inverted ? 'rgba(255,255,255,0.85)' : 'var(--v2-ink-muted)' }}>{feature}</span>
          </div>
        ))}
      </div>
      <a
        href={ctaHref}
        target="_blank"
        rel="noopener noreferrer"
        className="press hover-fade"
        style={{
          display: 'inline-block',
          background: inverted ? '#fff' : 'var(--v2-ink)',
          color: inverted ? 'var(--v2-primary)' : 'var(--v2-bg)',
          padding: '12px 24px',
          borderRadius: '999px',
          fontWeight: 700,
          fontSize: '14px',
          textDecoration: 'none',
        }}
      >
        {cta}
      </a>
    </div>
  );

  const grid = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '56px',
        alignItems: 'center',
        direction: reversed ? 'rtl' : 'ltr',
      }}
    >
      <div style={{ direction: 'ltr' }}>{textCol}</div>
      <div style={{ direction: 'ltr' }}>{art}</div>
    </div>
  );

  if (!inverted) return grid;

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, var(--v2-primary), var(--v2-violet-400))',
        borderRadius: '28px',
        padding: 'clamp(32px, 5vw, 64px)',
        boxShadow: 'var(--v2-shadow-strong)',
      }}
    >
      {grid}
    </div>
  );
};

/* ── Main component ────────────────────────────────────────── */

const FeatureSections = () => {
  const { t, i18n } = useTranslation();
  const storeUrl = getStoreUrl(i18n.language);

  const audioFeatures = Array.isArray(
    t('featureSections.audioFormat.features', { returnObjects: true })
  )
    ? (t('featureSections.audioFormat.features', { returnObjects: true }) as string[])
    : [
        'Instant AI transcription with 99% accuracy',
        'Smart summarization and key point extraction',
        'YouTube video summarization capability',
      ];

  const privacyFeatures = Array.isArray(
    t('featureSections.privacy.features', { returnObjects: true })
  )
    ? (t('featureSections.privacy.features', { returnObjects: true }) as string[])
    : [
        'End-to-end encryption for all your data',
        'Secure cross-device synchronization',
        'Complete data control and deletion rights',
      ];

  const summaryFeatures = Array.isArray(
    t('featureSections.summaries.features', { returnObjects: true })
  )
    ? (t('featureSections.summaries.features', { returnObjects: true }) as string[])
    : [
        'Meeting recaps with action items',
        'Key insights automatically extracted',
        'Custom summary templates',
      ];

  return (
    <div
      style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)', position: 'relative', zIndex: 2 }}
      className="px-4 py-16"
    >
      <div className="max-w-6xl mx-auto" style={{ display: 'flex', flexDirection: 'column', gap: '96px' }}>
        {/* Section 1 — Audio → Text (text left, art right) */}
        <FeatureSection
          label={t('featureSections.audioFormat.label')}
          title={t('featureSections.audioFormat.title')}
          description={t('featureSections.audioFormat.description')}
          features={audioFeatures}
          cta={t('featureSections.audioFormat.cta', 'Get Started Free')}
          ctaHref={storeUrl}
          art={<AudioSourceArt />}
        />

        {/* Section 2 — Privacy (text right, art left) — bold indigo block, our strongest differentiator */}
        <FeatureSection
          label={t('featureSections.privacy.label')}
          title={t('featureSections.privacy.title')}
          description={t('featureSections.privacy.description')}
          features={privacyFeatures}
          cta={t('featureSections.privacy.cta', 'See How We Protect You')}
          ctaHref={storeUrl}
          art={<PrivacyArt inverted />}
          reversed
          variant="indigo"
        />

        {/* Section 3 — Smart Summaries (text left, art right) */}
        <FeatureSection
          label={t('featureSections.summaries.label', 'SMART SUMMARIES')}
          title={t('featureSections.summaries.title', 'AI that reads between the lines')}
          description={t(
            'featureSections.summaries.description',
            'Notely AI doesn\'t just transcribe — it understands. Get instant meeting recaps, extracted action items, and key insights delivered the moment your recording ends.'
          )}
          features={summaryFeatures}
          cta={t('featureSections.summaries.cta', 'Download Now')}
          ctaHref={storeUrl}
          art={<SummaryArt />}
        />
      </div>
    </div>
  );
};

export default FeatureSections;
